<script setup>
import { onMounted, onBeforeUnmount, reactive, nextTick, ref, watch } from 'vue';
import WheelOfFortuneBannerContent from './WheelOfFortuneBannerContent.vue';
import WheelOfFurtuneCollapsibleContent from './WheelOfFurtuneCollapsibleContent.vue';
import WheelSoundToggle from './WheelSoundToggle.vue';
import { asset } from '../../helpers.js';
import { VOL_BG, WAVE_HEIGHTS } from './wheelAudio.js';

const props = defineProps({
    startAt: { type: Date, required: true },
    endAt: { type: Date, required: true },
    minOrderValue: { type: Number, required: true }
});

// --- Countdown ---
const state = reactive({ now: Date.now() });
let timer;

// --- Audio ---
const bgMusicSrc = asset('/hkcm/music/WheelOfFortune_backgoundMusic.mp3', 'public');
const bgMusic = ref(null);
const soundMuted = ref(false);
const soundPlaying = ref(false);

const startBgMusic = () => {
    if (soundMuted.value || !bgMusic.value || !bgMusic.value.paused) return;
    bgMusic.value.volume = VOL_BG;
    bgMusic.value.play().catch(() => {});
    soundPlaying.value = true;
};
const stopBgMusic = () => {
    if (!bgMusic.value) return;
    bgMusic.value.pause();
    bgMusic.value.currentTime = 0;
    soundPlaying.value = false;
};
const toggleSound = () => {
    soundMuted.value = !soundMuted.value;
    if (soundMuted.value) stopBgMusic();
    else if (isOpen.value) startBgMusic();
};

// --- Collapsible ---
const isOpen = ref(false);
const innerRef = ref(null);
const collapsibleRef = ref(null);
const contentHeight = ref(0);

const measure = () => {
    if (innerRef.value) contentHeight.value = innerRef.value.scrollHeight + 120;
};
const toggleOpen = async () => {
    isOpen.value = !isOpen.value;
    await nextTick();
    measure();

    if (isOpen.value) startBgMusic();
    else stopBgMusic();
};

const scrollToHowItWorks = () => {
    const howItWorksSection = document.getElementById('hkcm-wheel-so-funktioniert');

    if (howItWorksSection) {
        const headerOffset = 120;
        const y = Math.max(0, howItWorksSection.getBoundingClientRect().top + window.scrollY - headerOffset);

        window.scrollTo({ top: y, behavior: 'smooth' });
    }
};

const waitForOpenTransition = () => {
    return new Promise((resolve) => {
        const el = collapsibleRef.value;

        if (!el) {
            window.setTimeout(resolve, 520);
            return;
        }

        let done = false;
        const finish = () => {
            if (done) return;
            done = true;
            el.removeEventListener('transitionend', onTransitionEnd);
            resolve();
        };
        const onTransitionEnd = (event) => {
            if (event.target === el && event.propertyName === 'max-height') {
                finish();
            }
        };

        el.addEventListener('transitionend', onTransitionEnd);
        window.setTimeout(finish, 700);
    });
};

const onJoinNow = async () => {
    if (!isOpen.value) {
        isOpen.value = true;
        await nextTick();
        measure();
        startBgMusic();
        await waitForOpenTransition();
        await nextTick();
        scrollToHowItWorks();
        return;
    }

    scrollToHowItWorks();
};
const onResize = () => measure();

onMounted(() => {
    timer = window.setInterval(() => (state.now = Date.now()), 1000);
    measure();
    window.addEventListener('resize', onResize);
});
onBeforeUnmount(() => {
    if (timer) window.clearInterval(timer);

    window.removeEventListener('resize', onResize);
    stopBgMusic();
});
watch(
    () => props.endAt,
    async () => {
        await nextTick();
        measure();
    },
    { immediate: true }
);

// kleine Helfer
</script>

<template>
    <div class="relative">
    <audio ref="bgMusic" :src="bgMusicSrc" preload="auto" loop />
    <WheelSoundToggle v-if="isOpen" :sound-muted="soundMuted" :sound-playing="soundPlaying" :wave-heights="WAVE_HEIGHTS" @toggle="toggleSound" />
    <section class="relative rounded-3xl text-white p-3 sm:p-6 md:p-8">

        <div class="max-w-[1224px] mx-auto">

        <!-- Banner -->
        <WheelOfFortuneBannerContent
            :end-at="props.endAt"
            :is-open="isOpen"
            @show-more="toggleOpen"
            @join-now="onJoinNow"
        />

        <!-- Bereich für Collapsible -->
        <div class="relative mt-10 sm:mt-12">

            <!-- Collapsible -->
            <div id="hkcm-collapsible" ref="collapsibleRef" class="overflow-hidden transition-[max-height] duration-500 ease-in-out" :style="{ maxHeight: isOpen ? contentHeight + 'px' : '0px' }">
                <div
                    ref="innerRef"
                    class="pt-12 translate-y-2 transition-all duration-300 overflow-visible"
                    :class="isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'"
                >
                    <WheelOfFurtuneCollapsibleContent :end-at="props.endAt" :start-at="props.startAt" :min-order-value="props.minOrderValue" />
                    <!-- BOTTOM Button: nur wenn Collapsible open -->
                    <div v-if="isOpen" class="my-6 mx-2 sm:mx-6 flex justify-center">
                        <button
                            :aria-expanded="isOpen ? 'true' : 'false'"
                            aria-controls="hkcm-collapsible"
                            class="group inline-flex items-center gap-3 rounded-full px-6 py-3 bg-[#0b1428] text-white ring-2 ring-amber-400/50 border border-white/5 shadow-[0_10px_30px_rgba(0,0,0,.45)] hover:ring-amber-300/70 hover:brightness-[1.06] active:scale-[.99] transition"
                            @click="toggleOpen"
                        >
                            <span class="text-sm font-semibold">Weniger anzeigen</span>
                            <svg class="h-4 w-4 transition-transform duration-300 text-amber-300 rotate-180" viewBox="0 0 20 20" fill="currentColor">
                                <path
                                    fill-rule="evenodd"
                                    d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 10.94l3.71-3.71a.75.75 0 1 1 1.06 1.06l-4.24 4.24a.75.75 0 0 1-1.06 0L5.21 8.29a.75.75 0 0 1 .02-1.08z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </div>
        </div><!-- /max-w-[1224px] -->
    </section>
    </div>
</template>
